import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import * as net from 'util/Net';

import {SCIENTIFIC_MODEL} from '../ScientificModelConstants';

export const PREDICTION_CONFIG_SCHEMA_VERSION_V1 = 1;

export function aaSequenceId(id: string): {_type: 'aaSequenceId'; id: string} {
  return {_type: 'aaSequenceId', id};
}

export function moleculeId(id: string): {_type: 'moleculeId'; id: string} {
  return {_type: 'moleculeId', id};
}

export interface BulkPredictionItem {
  id: string;
  entityType: string;
}

// Molecules are excluded here because bulk predictions only support monomers and single-entity
// inputs; molecules are only meaningful as part of multimers, which are not yet supported in bulk.
const INPUT_TYPE_TO_FOLDER_ITEM_TYPE: Record<string, OligoAwareFolderItemType> = {
  amino_acid_sequence: ModelTypeConstants.PROTEIN,
};

export function getAvailableTypesForModel(inputTypes: Array<string>): Array<OligoAwareFolderItemType> {
  return inputTypes
    .map((t) => INPUT_TYPE_TO_FOLDER_ITEM_TYPE[t])
    .filter((t): t is OligoAwareFolderItemType => t != null);
}

export function isEntityTypeCompatible(entityType: string, inputTypes: Array<string>): boolean {
  const compatibleFolderItemTypes = getAvailableTypesForModel(inputTypes);
  return compatibleFolderItemTypes.includes(entityType as OligoAwareFolderItemType);
}

export type PredictionConfig = Record<string, unknown>;

type PredictionConfigBuilder = (item: BulkPredictionItem) => PredictionConfig;

const aaSequenceConfigBuilder: PredictionConfigBuilder = (item) => ({
  component: aaSequenceId(item.id),
});
const boltzGenConfigBuilder: PredictionConfigBuilder = (item) => ({
  targets: [item.entityType === ModelTypeConstants.MOLECULE ? moleculeId(item.id) : aaSequenceId(item.id)],
});
const multimerListConfigBuilder: PredictionConfigBuilder = (item) => ({
  components: [aaSequenceId(item.id)],
});

const PREDICTION_CONFIG_BUILDERS: Record<string, PredictionConfigBuilder> = {
  [SCIENTIFIC_MODEL.ALPHAFOLD]: aaSequenceConfigBuilder,
  [SCIENTIFIC_MODEL.BOLTZGEN]: boltzGenConfigBuilder,
  [SCIENTIFIC_MODEL.OPENFOLD2]: aaSequenceConfigBuilder,
  [SCIENTIFIC_MODEL.OPENFOLD3]: multimerListConfigBuilder,
  [SCIENTIFIC_MODEL.CHAI1]: multimerListConfigBuilder,
  [SCIENTIFIC_MODEL.BOLTZ1]: multimerListConfigBuilder,
  [SCIENTIFIC_MODEL.BOLTZ2]: multimerListConfigBuilder,
  [SCIENTIFIC_MODEL.PROTENIX]: multimerListConfigBuilder,
};

export function buildPredictionConfigs(
  model: string,
  items: Array<BulkPredictionItem>
): Array<PredictionConfig> {
  const builder = PREDICTION_CONFIG_BUILDERS[model] ?? aaSequenceConfigBuilder;
  return items.map((item) => builder(item));
}

export interface BulkPredictionSubmitParams {
  folderId: string;
  model: string;
  items?: Array<BulkPredictionItem>;
  predictionConfigs?: Array<PredictionConfig>;
  modelParams?: Record<string, unknown>;
  source?: PredictionBatchSource;
  name?: string;
}

export async function submitBulkPrediction(
  params: BulkPredictionSubmitParams
): Promise<{predictionBatchId: string}> {
  const {folderId, model, items, modelParams, source, name} = params;
  if (params.predictionConfigs == null && items == null) {
    throw new Error('Must provide items or prediction configs');
  }
  if (params.predictionConfigs != null && items != null) {
    throw new Error('Must provide only one of items or prediction configs');
  }
  const predictionConfigs = params.predictionConfigs ?? buildPredictionConfigs(model, items ?? []);

  const inputConfig: Record<string, unknown> = {
    predictionConfigs,
    ...modelParams,
  };

  return await net.postJSONPromise({
    url: '/1/api/model-hub/prediction-batch',
    data: {
      folderId,
      model,
      inputConfig,
      configSchemaVersion: PREDICTION_CONFIG_SCHEMA_VERSION_V1,
      source,
      name,
    },
  });
}
